import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { LogbookLeg } from '@shared/models/activity-summary/logbook-leg';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { LatLngExpression } from 'leaflet'
import { designateTagType } from '@shared/utilities/utilities';
import { ButtonModule } from 'primeng/button';
import { FieldsetModule } from 'primeng/fieldset';
import { FormatTimePipe, FormatMinutes } from '@shared/pipes/format-time.pipe';
import { FlightdetailService } from './service/flightdetail.service';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import {
  catchError,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
}
  from 'rxjs';
import { FlightCrew } from '@shared/models/activity-summary/flight-crew';
import { SignalsService } from '@shared/services/signals/signals.service';
import { CrewDetail } from './model/crew-detail';
import { MapService } from '../services/map.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CrewContactInfoComponent } from './crew-contact-info/crew-contact-info.component';
import { SearchResult } from 'app/features/search/model/search-result';
import { DateTime } from 'luxon';
import { AppService } from 'app/app.service';
import { AppSettings } from '@shared/models/settings/app-settings';
import { ErrorLogService } from '@shared/services/errorhandler/error-log.service';
import { FlightDetailDialogComponent } from './flight-detail-dialog/flight-detail-dialog.component';
import { ActivatedRoute } from '@angular/router';
import { ActivityRecordService } from '@shared/services/activity-record/activity-record.service';
import { BreadcrumbService } from '@shared/components/breadcrumb/breadcrumb.service';
import { Aircraft } from '@shared/models/activity-summary/aircraft';
import { RegDetailsDialogComponent } from './reg-details-dialog/reg-details-dialog.component';
import { AIRLINE_CODES } from '@shared/utilities/airline-codes';
import { FlightsService } from '../services/flight.service';
import { DetailNotFoundComponent } from '../detail-not-found/detail-not-found.component';
import { Sequences } from '@shared/models/activity-summary/sequences';
import { ConvertDistancePipe } from '@shared/pipes/convert-distance.pipe';
import { PopoverModule } from 'primeng/popover';

@Component({
    selector: 'app-flight-detail',
    imports: [
        CommonModule,
        LeafletModule,
        ButtonModule,
        FieldsetModule,
        FormatTimePipe,
        FormatMinutes,
        DatePipe,
        BreadcrumbComponent,
        ConvertDistancePipe,
        PopoverModule
    ],
    templateUrl: './flight-detail.component.html',
    styleUrl: './flight-detail.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FlightDetailComponent implements OnInit, OnDestroy {

  flightLeg$?: Observable<LogbookLeg>;
  crewMembers$?: Observable<RequestState<CrewDetail[]>>;
  ref: DynamicDialogRef | undefined;
  airlineCodes = AIRLINE_CODES;
  isTimeFormatUTC?: boolean;
  subRoute?: Subscription;
  subTheme?: Subscription;
  theme$?: string;
  tag: string = '---';
  oalCode: string = '';

  //Used when navigation from search results
  searchResponse?: SearchResult;

  takeOffIconLight = '/assets/icons/aircraft/takeoffLight.svg';
  takeOffIconDark = '/assets/icons/aircraft/takeoffDark.svg';
  landingIconLight = '/assets/icons/aircraft/landingLight.svg';
  landingIconDark = '/assets/icons/aircraft/landingDark.svg';
  contactLight = '/assets/icons/contact/contactIconLight_ver1.svg';
  contactDark = '/assets/icons/contact/contactIconDark_ver1.svg';
  sequenceNumber: number = 0;
  year: number = 0;
  month: number = 0;
  originationDate: string = '';
  flightNumber: number = 0;
  depAirport: string = '';
  arrAirport: string = '';
  flightDate: string = '';

  constructor(
    public appService: AppService,
    private flightDetailService: FlightdetailService,
    private signalsService: SignalsService,
    private mapService: MapService,
    private dialogService: DialogService,
    private errorLogService: ErrorLogService,
    private flightService: FlightsService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private activityRecordService: ActivityRecordService,
    private breadcrumbService: BreadcrumbService
  ) { }

  ngOnInit(): void {

    //update breadcrumb service
    const url = location.pathname;
    const breadcrumbLabel = this.route.snapshot.data['breadcrumb'] || 'Flight';
    this.breadcrumbService.addToBreadcrumbs(url, breadcrumbLabel);

    this.subTheme = this.appService.theme$.subscribe((theme) => {
      this.theme$ = theme;
      this.cdr.markForCheck();
    });

    // Get user current user settings for date/time format
    const settings = localStorage.getItem('appSettings');
    if (settings) {
      const parsedSettings: AppSettings = JSON.parse(settings);
      this.isTimeFormatUTC = parsedSettings.dateTimeFormatDefault === 'utc' ? true : false;
    }

    this.subRoute = this.route.paramMap.subscribe((params) => {
      this.sequenceNumber = Number(params.get('number'));
      this.year = Number(params.get('year'));
      this.month = Number(params.get('month'));
      this.originationDate = params.get('originationDate') || '';
      this.flightNumber = Number(params.get('flightnNumber'));
      this.depAirport = params.get('depAirport') || '';
      this.arrAirport = params.get('arrAirport') || '';
      this.flightDate = params.get('flightDate') || '';
    });

    /**Full Sequence loaded from Search-Results component */
    const unfilteredResults = sessionStorage.getItem('unfilteredResults');
    if (unfilteredResults) {
      const sequenceParsed = JSON.parse(unfilteredResults);
      const sequence = sequenceParsed as SearchResult;

      this.flightLeg$ = of(sequence).pipe(
        map((data) => {
          const leg = this.getLegDetails(data.items, this.originationDate);

          if (leg) {
            this.renderMap(leg);
            this.crewMembers$ = this.getCrewData(leg);
            this.tag = designateTagType(leg?.isDeadhead, leg?.isCancelled);

            return leg;
          }
          throw new Error('Leg not found');
        })
      )

    }
    /**Load flight details from Table View Component*/
    else {
      this.flightLeg$ = this.fetchFlightDetail(
        this.year,
        this.month,
        this.originationDate
      ).pipe(
        filter((data) => data.response != null),
        map((data) => {
          return data?.response as LogbookLeg;
        }),
        catchError((err) => {
          console.error('Flight Details', err);
          const fltDetails = {
            sequenceNumber: this.sequenceNumber,
            year: this.year,
            month: this.month,
            originationDate: this.originationDate,
            flightNumber: this.flightNumber,
            depAirport: this.depAirport,
            arrAirport: this.arrAirport,
            flightDate: this.flightDate
          }
          this.flightNotFound(fltDetails);
          return EMPTY;
        })
      );
    }
  }

  flightNotFound(flightDetails: any): void {
    this.ref = this.dialogService.open(DetailNotFoundComponent, {
      data: {
        id: 'flt',
        data: flightDetails
      },
      header: 'NOT FOUND',
      width: '50vw',
      modal: true,
      dismissableMask: false,
      breakpoints: {
        '500px': '98vw',
      }
    });
  }

  fetchFlightDetail(
    year: number,
    month: number,
    originationDate: string
  ): Observable<RequestState<LogbookLeg>> {

    return this.signalsService.employeeNumber$.pipe(
      switchMap(employeeNumber => {
        if (!employeeNumber) {
          this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: No employee number found: Flight Detail`);
        }
        return this.activityRecordService.getSummaryRecords(employeeNumber, year, month)
          .pipe(
            map((data) => {
              if (!data || !data.response || data.isLoading) {
                return new RequestState<LogbookLeg>(
                  RequestStatus.IsCompleteNoResponse);
              }
              const leg = this.getLegDetails(data.response.sequences, originationDate);

              if (!leg) {
                throw new Error('Details not found');
              }
              this.tag = designateTagType(leg?.isDeadhead, leg?.isCancelled);
              if (leg.isDeadhead) {
                this.oalCode = leg.oalSurfaceAirline || '';
              }
              this.renderMap(leg);
              this.crewMembers$ = this.getCrewData(leg);
              return new RequestState<LogbookLeg>(
                RequestStatus.IsCompleteWithResponse,
                leg
              );
            })
          )
      })
    );
  }

  getLegDetails(sequences: Sequences[], originationDate: string): LogbookLeg {
    const sequence = sequences.find((seq) => {
      const seqOriginationDate = DateTime.fromISO(
        seq.originationDate, {zone: 'utc'}).toFormat('yyyy-MM-dd');

      return seq.sequenceNumber === this.sequenceNumber && seqOriginationDate === originationDate;
    });

    if (!sequence) {
      throw new Error('Sequence not found');
    }
    const leg = sequence.dutyPeriodSummaries.flatMap((duty) =>
      duty.legs.filter((leg) => {
        const depTimeUTC = DateTime.fromISO(
          leg.departure.scheduled.utc, {zone: 'utc'}).toFormat('yyyy-MM-dd');
        const depTimeLocal = DateTime.fromISO(
          leg.departure.scheduled.local, {zone: 'utc'}).toFormat('yyyy-MM-dd');
        const depTime = this.isTimeFormatUTC ? depTimeUTC : depTimeLocal;
        return (
          leg.flightNumber === this.flightNumber &&
          leg.departureStation.code === this.depAirport &&
          leg.arrivalStation.code === this.arrAirport &&
          depTime === this.flightDate)
      })
    )[0];
    if (leg) {
      return leg;
    }
    throw new Error('Leg not found');
  }

  renderMap(leg: LogbookLeg): void {
    if (leg) {
      const geoLineColor = this.getGeoLineColor(leg);
      const coords: LatLngExpression[] = [
        [
          leg.departureStation.latitude || 0,
          leg.departureStation.longitude || 0,
        ],
        [
          leg.arrivalStation.latitude || 0,
          leg.arrivalStation.longitude || 0,
        ],
      ];

      this.mapService.renderMap('legMap', coords, [], [], false, geoLineColor);
    }

  }

  ngOnDestroy(): void {
    this.subTheme?.unsubscribe();
    this.subRoute?.unsubscribe();
  }

  onResetMap(): void {
    this.mapService.initMap('legMap');
  }

  convertDate(date?: string): string | undefined {
    if (date) {
      const dateTime = DateTime.fromISO(date, { zone: 'utc' });
      return dateTime.toFormat('dd MMM yyyy');
    }
    return undefined;
  }


  onContactCrew(crewMember: CrewDetail): void {
    this.flightDetailService._requestedEmpNum$.next(crewMember.username);

    this.ref = this.dialogService.open(CrewContactInfoComponent, {
      header: 'Crew Member Contact',
      width: '50vw',
      modal: true,
      dismissableMask: true,
      closable: true,
      breakpoints: {
        '500px': '98vw',
      }
    });
  }

  onHelpClicked(id: number): void {
    const header = id === 0 ? 'Flight Details' : 'Flight Times';
    this.ref = this.dialogService.open(FlightDetailDialogComponent, {
      data: id,
      header: header,
      width: '50vw',
      modal: true,
      dismissableMask: true,
      closable: true,
      breakpoints: {
        '500px': '98vw',
      }
    });
  }

  onRegClicked(aircraft: Aircraft): void {
    this.ref = this.dialogService.open(RegDetailsDialogComponent, {
      data: aircraft,
      header: 'Aircraft Details',
      width: '50vw',
      modal: true,
      dismissableMask: true,
      closable: true,
      breakpoints: {
        '500px': '98vw',
      }
    });
  }

  getGeoLineColor(leg: LogbookLeg): string {
    if (leg.isCancelled) {
      return 'red';
    }
    else {
      return 'blue'
    }
  }

  getCrewData(leg: LogbookLeg): Observable<RequestState<CrewDetail[]>> {
    let crewEmpNumbers: any[] = [];
    let crews = [];
    if (!leg) {
      console.error('CrewDetails Error, no leg information')
      this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: Get crew data, no leg information`)
    }
    else {
      for (let crew of leg.flightCrew) {
        crewEmpNumbers.push(crew.employeeNumber)
        crews.push({ employeeNumber: crew.employeeNumber, seat: crew.seat })
      }
    }
    const cacheId = `crew.seq${this.sequenceNumber}.flt${leg.flightNumber}.${leg.index}`
    return this.signalsService.employeeNumber$.pipe(
      switchMap(() => {
        return this.flightDetailService.getCrewDetailsFor(crewEmpNumbers, cacheId)
          .pipe(
            map((crewDetail) => {
              if (!crewDetail || !crewDetail.response || crewDetail.isLoading) {
                return new RequestState<CrewDetail[]>(
                  RequestStatus.IsCompleteNoResponse
                )
              }
              let crewMembers: CrewDetail[] = [];

              //Loop through crew details and match seat position
              for (let crew of crewDetail.response || []) {
                const crewMember = {
                  username: crew.username,
                  firstName: crew.firstName,
                  lastName: crew.lastName,
                  middleName: crew.middleName,
                  nickName: crew.nickName,
                  seat: this.matchSeatPosition(crew, crews)
                }
                crewMembers.push(crewMember);
              }
              
              return new RequestState<CrewDetail[]>(
                RequestStatus.IsCompleteWithResponse,
                crewMembers
              )
            })
          )
      })
    );
  }

  validateAircraftId(aircraftId?: string): string {
    return this.flightService.validateAircraft(aircraftId);
  }

  matchSeatPosition(crewDetail: CrewDetail, flightCrews: FlightCrew[]): string {
    for (let flightcrew of flightCrews) {
      if (crewDetail.username === flightcrew.employeeNumber) {
        return flightcrew.seat
      }
    }
    return ''
  }

  // See main.scss for fieldset styles global styles
  fieldSet = {
    root: {
      background: 'var(--p-surface-0)',
    }
  }

  popoverStyle = {
    root: {
      arrowOffset: '20px',
    }
  }
}
